import { createStore } from 'vuex';
import axios from 'axios';

interface User {
  id: number;
  numero_compte: number;
  name: string;
  nom: string;
  prenom: string;
  email: string;
  telephone1: number;
  telephone2: number;
  adresse: string;
  date_naissance: null | string;
  sexe: string;
  type: string;
  sous_type: string;
  poste: string;
  description: string;
  photo: string;
  is_superadmin: number;
  is_disabled: number;
  raison_sociale: string;
  sigle: string;
  bp: number;
  types_permis: string;
  commercial: string;
  created_at: string;
}

// Définir le type de l'état
interface State {
  apiUrl: string;
  authenticated: boolean;
  user: User | null; // Un utilisateur unique
  token: string;
  notificationCount: number;
  networkError: string | null;
  isOnline: boolean;
  isLoading: boolean;
}

interface AuthPayload {
  user: User;
  token: string;
}

// Import du module statistiques
import statistiques from './modules/statistiques';

export default createStore({
  state: {
    // apiUrl: 'http://localhost:9001',
    apiUrl: 'https://api.logistics.yams-ga.com',
    authenticated: false,
    user: null,
    token: '',
    notificationCount: 0,
    networkError: null,
    isOnline: navigator.onLine,
    isLoading: false
  } as State,
  getters: {
    notificationCount: (state: State) => state.notificationCount,
    authenticated: (state: State) => state.authenticated,
    user: (state: State) => state.user,
    apiUrl: (state: State) => state.apiUrl,
    networkError: (state: State) => state.networkError,
    isOnline: (state: State) => state.isOnline,
    isLoading: (state: State) => state.isLoading
  },
  mutations: {
    setAuthenticated(state: State, payload: AuthPayload) {
      state.user = payload.user;
      state.token = payload.token;
      state.authenticated = true;
      const userDatas = {
        user: payload.user,
        token: payload.token,
      };
      window.localStorage.setItem('yamsmanager_session', JSON.stringify(userDatas));
    },
    setUser(state: State, user: User) {
      if (state.user) {
        state.user.nom = user.nom;
        state.user.prenom = user.prenom;
        state.user.adresse = user.adresse;
      }
      const userDatas = {
        user: state.user,
        token: state.token,
      };
      window.localStorage.setItem('yamsmanager_session', JSON.stringify(userDatas));
    },
    setUserPhoto(state: State, photo: string) {
      if (state.user) {
        state.user.photo = photo;
      }
      const userDatas = {
        user: state.user,
        token: state.token,
      };
      window.localStorage.setItem('yamsmanager_session', JSON.stringify(userDatas));
    },
    setNotifications(state: State, notifications: any[]) {
      state.notificationCount = notifications.length;
    },
    clearUserData(state: State) {
      state.user = null;
      state.token = '';
      state.authenticated = false;
      window.localStorage.removeItem('yamsmanager_session');
    },
    
    // Nouvelles mutations pour gérer les erreurs réseau
    setNetworkError(state: State, errorMessage: string | null) {
      state.networkError = errorMessage;
    },
    
    setOnlineStatus(state: State, isOnline: boolean) {
      state.isOnline = isOnline;
    },
    
    // Mutation pour gérer l'état de chargement
    setIsLoading(state: State, isLoading: boolean) {
      state.isLoading = isLoading;
    }
  },  actions: {
    async fetchNotifications({ commit }) {
      try {
        const response = await axios.get('/api/notifications?read_at=null&req_count');
        commit('setNotifications', response.data);
      } catch (error) {
        console.error('Erreur lors de la récupération des notifications:', error);
      }
    },
    async fetchMessages({ commit }) {
      try {
        const response = await axios.get('/api/messages?read_at=&req_count');
        commit('setMessages', response.data);
      } catch (error) {
        console.error('Erreur lors de la récupération des messages:', error);
      }
    },
    
    // Nouvelles actions pour gérer les erreurs réseau
    checkNetworkStatus({ commit }) {
      const isOnline = navigator.onLine;
      commit('setOnlineStatus', isOnline);
      
      if (!isOnline) {
        commit('setNetworkError', 'Pas de connexion Internet. Veuillez vérifier votre connexion réseau.');
      } else {
        commit('setNetworkError', null);
      }
    },
    
    retryFailedRequest({ commit }, request) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          axios(request)
            .then(response => resolve(response))
            .catch(error => reject(error));
        }, 2000); // Réessayer après 2 secondes
      });
    }
  },
  modules: {
    statistiques // Module de statistiques pour le tableau de bord
  },
});
